import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { appsyncQuery } from '../../../api/appsync-client.js'
import { useSubscription } from '../../../api/useSubscription.js'
import {
  TASK_CREATED_FOR_PAIR,
  TASK_DELETED_FOR_PAIR,
  TASK_UPDATED_FOR_PAIR,
  TASKS_FOR_PAIR,
} from '../graphql.js'
import { sortTasks } from '../utils/sorting.js'

/**
 * Centralizes the data plumbing for the TaskList component.
 *
 * In `live` mode it fetches `tasksForPair`, subscribes to the three pair
 * subscriptions, merges events into local state by `taskId`, and applies
 * client-side sort + status filter.
 *
 * In `snapshot` mode it skips every API call and renders the
 * `snapshotTasks` prop directly. The prop is deep-cloned once on mount so
 * downstream sorting can never mutate the parent's frozen blob.
 *
 * @param {object} params
 * @param {string} params.reportId
 * @param {string} params.managerId
 * @param {'live'|'snapshot'} params.mode
 * @param {Array} [params.snapshotTasks]
 * @param {Array<string>} [params.statusFilter]
 * @param {boolean} [params.softCloseFilter] - capture the open-task ID
 *   set on first load and keep those rows visible (with a `closed`
 *   flag) even after they transition to DONE/CANCELLED. Tasks that
 *   weren't open at mount stay hidden. The next time the component
 *   mounts (e.g. after a page reload) those done rows are gone.
 * @param {string} params.sortField
 * @param {'asc'|'desc'} params.sortDirection
 */
const OPEN_STATUSES = new Set(['TODO', 'IN_PROGRESS'])

export function useTaskList({
  reportId,
  managerId,
  mode,
  snapshotTasks,
  statusFilter,
  softCloseFilter = false,
  sortField,
  sortDirection,
}) {
  const isSnapshot = mode === 'snapshot'

  // Snapshot mode owns its data once, frozen at mount.
  const frozenSnapshot = useMemo(
    () => (isSnapshot ? (snapshotTasks ?? []).map((t) => ({ ...t })) : null),
    // Re-clone if the parent passes a different array instance — but never
    // mutate the parent's array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSnapshot, snapshotTasks],
  )

  const [liveTasks, setLiveTasks] = useState([])
  const [loading, setLoading] = useState(!isSnapshot)
  const [error, setError] = useState(null)

  // softCloseFilter: capture the open-task ID set on the first successful
  // fetch. Mutating subscriptions (or local optimistic updates) move tasks
  // from open → done within this set; the visibility predicate keeps them
  // around so the user can see what they closed during the meeting. The
  // ref is intentionally NOT reset across re-renders — the next mount
  // (page reload) gets a fresh capture.
  const openAtMountRef = useRef(null)
  const captureOpenSet = useCallback((tasks) => {
    if (!softCloseFilter) return
    if (openAtMountRef.current !== null) return
    openAtMountRef.current = new Set(
      tasks.filter((t) => OPEN_STATUSES.has(t.status)).map((t) => t.taskId),
    )
  }, [softCloseFilter])

  const fetchLive = useCallback(async () => {
    if (isSnapshot || !reportId || !managerId) return
    setLoading(true)
    setError(null)
    try {
      const data = await appsyncQuery(TASKS_FOR_PAIR, {
        reportId,
        managerId,
        statusFilter: null,
      })
      const items = data?.tasksForPair ?? []
      captureOpenSet(items)
      setLiveTasks(items)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [isSnapshot, reportId, managerId, captureOpenSet])

  useEffect(() => {
    fetchLive()
  }, [fetchLive])

  const subVars = useMemo(() => ({ reportId, managerId }), [reportId, managerId])
  const subEnabled = !isSnapshot && !!reportId && !!managerId

  useSubscription(
    TASK_CREATED_FOR_PAIR,
    subVars,
    (data) => {
      const t = data?.taskCreatedForPair
      if (!t) return
      setLiveTasks((prev) => {
        if (prev.some((x) => x.taskId === t.taskId)) return prev
        return [...prev, t]
      })
    },
    { enabled: subEnabled },
  )

  useSubscription(
    TASK_UPDATED_FOR_PAIR,
    subVars,
    (data) => {
      const t = data?.taskUpdatedForPair
      if (!t) return
      setLiveTasks((prev) => prev.map((x) => (x.taskId === t.taskId ? t : x)))
    },
    { enabled: subEnabled },
  )

  useSubscription(
    TASK_DELETED_FOR_PAIR,
    subVars,
    (data) => {
      const t = data?.taskDeletedForPair
      if (!t) return
      setLiveTasks((prev) => prev.filter((x) => x.taskId !== t.taskId))
    },
    { enabled: subEnabled },
  )

  // Local optimistic mutators — used by useTaskMutations to apply changes
  // before the network round-trip and to roll back on failure.
  const optimisticUpdate = useCallback((taskId, patch) => {
    setLiveTasks((prev) => prev.map((t) => (t.taskId === taskId ? { ...t, ...patch } : t)))
  }, [])
  const optimisticInsert = useCallback((task) => {
    setLiveTasks((prev) => {
      if (prev.some((t) => t.taskId === task.taskId)) return prev
      return [...prev, task]
    })
  }, [])
  const optimisticRemove = useCallback((taskId) => {
    setLiveTasks((prev) => prev.filter((t) => t.taskId !== taskId))
  }, [])
  const restoreSnapshot = useCallback((snapshot) => {
    setLiveTasks(snapshot)
  }, [])
  const snapshotState = useCallback(() => liveTasks.map((t) => ({ ...t })), [liveTasks])

  const visibleTasks = useMemo(() => {
    const source = isSnapshot ? frozenSnapshot : liveTasks
    let filtered = source ?? []
    if (softCloseFilter && !isSnapshot) {
      const openAtMount = openAtMountRef.current ?? new Set()
      filtered = filtered.filter(
        (t) => OPEN_STATUSES.has(t.status) || openAtMount.has(t.taskId),
      )
    } else if (statusFilter && statusFilter.length > 0) {
      const allowed = new Set(statusFilter)
      filtered = filtered.filter((t) => allowed.has(t.status))
    }
    return sortTasks(filtered, sortField, sortDirection)
  }, [
    isSnapshot,
    frozenSnapshot,
    liveTasks,
    statusFilter,
    softCloseFilter,
    sortField,
    sortDirection,
  ])

  return {
    tasks: visibleTasks,
    loading: isSnapshot ? false : loading,
    error: isSnapshot ? null : error,
    refetch: fetchLive,
    // Hooks below are private to useTaskMutations.
    _optimisticUpdate: optimisticUpdate,
    _optimisticInsert: optimisticInsert,
    _optimisticRemove: optimisticRemove,
    _snapshotState: snapshotState,
    _restoreSnapshot: restoreSnapshot,
  }
}
